import React, { useMemo, useState } from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { useRootStore } from '~/stores/useRootStore';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import styled from 'styled-components';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import { observer } from 'mobx-react';
import { trackError } from '~/services/logger/error.utils';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import useAnalytics from '~/common/useAnalytics';
import HBMediaElement from '@honeybook/hbui/dist/components/HBMediaElement/HBMediaElement';
import { getClickShareTemplateSource } from '~/components/Templates/templates.utils';
import HBLink from '@honeybook/hbui/dist/components/HBLink/HBLink';
import Web24 from '@honeybook/icons/dist/icons/Web24';
import { useCampaignDetails } from '~/components/Referral/components/ReferralsPage/hooks/useCampaignDetails';

type TBulletPoint = {
    imagePath: string;
    alt: string;
    textKey: TFuncKey<
        typeof NAMESPACES.TEMPLATES,
        'share_template.pre_publish'
    >;
};

const PrePublishTemplateModalContent = () => {
    const {
        templatesUiStore: {
            selectedTemplate,
            updateTemplatePublicConfiguration
        },
        templatesCtrl,
        flowStore
    } = useRootStore();
    const {
        toastStore,
        userStore: { currUser }
    } = useGlobalMX();
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.pre_publish'
    });
    const [isLoading, setIsLoading] = useState(false);
    const { isMediumUp } = useWindowSize();
    const { track } = useAnalytics();
    const { campaignDetails: { max_commission_amount } = {} } =
        useCampaignDetails();

    const bulletPoints: TBulletPoint[] = useMemo(
        () => [
            {
                imagePath: 'app/public_template_sharing/glob.png',
                alt: 'world icon',
                textKey: 'share_anywhere'
            },
            {
                imagePath: 'app/public_template_sharing/money.png',
                alt: 'cash icon',
                textKey: currUser?.isEducator
                    ? 'referral_educators'
                    : 'referral'
            },
            {
                imagePath: 'app/public_template_sharing/cart.png',
                alt: 'cart icon',
                textKey: 'sell'
            }
        ],
        [currUser?.isEducator]
    );

    const generateLink = async () => {
        track('generate share link', {
            action: 'click',
            source: getClickShareTemplateSource(),
            template_id: selectedTemplate?._id
        });
        try {
            setIsLoading(true);
            const res = await templatesCtrl.publishTemplate(
                selectedTemplate?._id
            );
            updateTemplatePublicConfiguration(res);
            if (flowStore.flows[res._id]) {
                flowStore.updateFlow(res._id, {
                    public_configuration: res.public_configuration
                });
            }
            if (isMediumUp) {
                toastStore.add({
                    text: t('success_toast'),
                    type: ToastType.Success,
                    icon: Web24
                });
            }
        } catch (error) {
            trackError({
                error,
                message: '[Public Template] An error on sharing template',
                context: { flowId: selectedTemplate?._id }
            });
            toastStore.add({
                type: ToastType.Error,
                text: t('publish_error')
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PrePublishModal>
            <Description>
                <HBText type="body14">
                    {t('description')}{' '}
                    <HBText type="emphasis14" color="blue20">
                        <HBLink
                            isNewTab
                            noUnderline
                            testIdName="sharing-learn-more"
                            textType="body14"
                            href="https://help.honeybook.com/en/articles/9162513-sharing-your-templates-publicly"
                        >
                            <b>{t('learn_more')}</b>
                        </HBLink>
                    </HBText>
                </HBText>
            </Description>
            <Points>
                {bulletPoints.map(({ imagePath, alt, textKey }) => (
                    <Point key={textKey}>
                        <StyledBulletPointImage
                            path={imagePath}
                            alt={alt}
                            width={32}
                        />
                        <HBText type="body14">
                            {t(textKey, { amount: max_commission_amount })}
                        </HBText>
                    </Point>
                ))}
            </Points>
            <GenerateLinkCta
                id="generate-link"
                testIdName="generate-link"
                onClick={generateLink}
                loading={isLoading}
            >
                {t('cta')}
            </GenerateLinkCta>
        </PrePublishModal>
    );
};

export default observer(PrePublishTemplateModalContent);

const PrePublishModal = styled.div`
    max-width: 464px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 32px;
`;

const Description = styled.div``;

const Points = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

const Point = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
`;

const GenerateLinkCta = styled(HBButton)`
    width: 100%;
    align-items: center;
`;

const StyledBulletPointImage = styled(HBMediaElement)`
    min-width: 32px;
    height: 32px;
`;
